/**
 * "Mark not required" reason modal (Slice M / ADR-0100). Opened from a
 * ✗-state artifact slot on the Firm Detail Panel; the free-text reason is
 * REQUIRED (locked decision 2) and lands in audit_log via
 * `intake.exemption.marked`. Re-affirming an exemption after a designation
 * change reuses this modal — same action, fresh reason.
 */

import { useState } from "react";
import { Button } from "@/components/ui/button";

export function MarkNotRequiredModal({
  firmName,
  artifactLabel,
  pending,
  onSubmit,
  onClose,
  testId = "mark-not-required-modal",
}: {
  firmName: string;
  artifactLabel: string;
  pending: boolean;
  onSubmit: (reason: string) => void;
  onClose: () => void;
  testId?: string;
}) {
  const [reason, setReason] = useState("");
  const trimmed = reason.trim();

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-[#000527]/40 p-4"
      role="presentation"
      onMouseDown={onClose}
      data-testid={`${testId}-overlay`}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={`Mark ${artifactLabel} not required for ${firmName}`}
        className="w-full max-w-md rounded-md border bg-card text-card-foreground p-4 shadow-xl"
        onMouseDown={(e) => e.stopPropagation()}
        data-testid={testId}
      >
        <h2 className="text-sm font-sans font-semibold">
          Mark {artifactLabel} not required
        </h2>
        <p className="mt-1 text-xs text-muted-foreground">
          {firmName} will stop appearing on the Intake Gaps chase list for{" "}
          {artifactLabel}. A reason is required and is recorded in the audit
          log.
        </p>
        <textarea
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          disabled={pending}
          autoFocus
          rows={3}
          maxLength={1000}
          placeholder={`Why is ${artifactLabel} not required for this firm?`}
          className="mt-3 w-full rounded-sm border bg-background px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-ring disabled:opacity-60"
          data-testid={`${testId}-reason`}
        />
        <div className="mt-3 flex justify-end gap-2">
          <Button
            size="sm"
            variant="outline"
            onClick={onClose}
            disabled={pending}
            data-testid={`${testId}-cancel`}
          >
            Cancel
          </Button>
          <Button
            size="sm"
            onClick={() => onSubmit(trimmed)}
            disabled={pending || trimmed.length === 0}
            data-testid={`${testId}-submit`}
          >
            {pending ? "Saving…" : "Mark not required"}
          </Button>
        </div>
      </div>
    </div>
  );
}
